import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATES = ["new", "triaged", "escalated", "resolved"];
const ACTIONS = [
  { id: "mark_triaged",          label: "Mark triaged" },
  { id: "escalate",              label: "Escalate", danger: true },
  { id: "restrict_account_demo", label: "Restrict account (demo)", danger: true },
  { id: "suspend_account_demo",  label: "Suspend account (demo)", danger: true },
  { id: "resolve",               label: "Resolve" },
];

export default function HQSafety() {
  const [state, setState] = useState("new");
  const [items, setItems] = useState([]);
  const [reason, setReason] = useState({});

  const load = () => api.get(`/hq/safety?state=${state}`).then((r) => setItems(r.data || []));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [state]);

  const act = async (rid, action) => {
    const r = reason[rid] || "";
    if (["escalate", "restrict_account_demo", "suspend_account_demo"].includes(action) && !r.trim()) {
      toast.error("Reason required for this action"); return;
    }
    try {
      await api.post(`/hq/safety/${rid}/action`, { action, reason: r });
      toast.success(`Safety: ${action}`); setReason({ ...reason, [rid]: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  return (
    <div className="p-6" data-testid="hq-safety">
      <p className="overline">Safety incidents</p>
      <h1 className="font-display text-3xl tracking-tighter mb-2">Safety Centre</h1>
      <p className="text-xs text-cream/60 mb-6">Serious safety incidents have <b>no time limit</b>. Do not automatically suspend anyone because a report exists.</p>
      <div className="flex gap-2 mb-4 flex-wrap">
        {STATES.map((s) => (
          <button key={s} data-testid={`hq-safety-filter-${s}`} onClick={() => setState(s)}
                  className={`chip text-[10px] ${state === s ? "border-gold text-gold bg-gold/5" : ""}`}>{s}</button>
        ))}
      </div>
      <div className="space-y-4">
        {items.length === 0 && <div className="border border-cream/10 p-4 text-center text-cream/50 text-xs" data-testid="hq-safety-empty">No incidents in this state.</div>}
        {items.map((r) => (
          <div key={r.id} className={`border p-5 ${r.severity === "high" ? "border-red-400/50" : "border-cream/10"}`} data-testid={`hq-safety-${r.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <p className="font-display text-lg">{r.subject}</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-cream/50">Reporter: {r.reporter_name} · Reported: {r.reported_user_id ? r.reported_user_id.slice(0, 8) + "…" : "—"} · Severity: <span className={r.severity === "high" ? "text-red-300" : ""}>{r.severity}</span></p>
                <p className="text-xs text-cream/80 mt-2">{r.description}</p>
                <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-cream/50 grid grid-cols-2 gap-y-1">
                  <span>Studio: {r.booking_summary?.studio_name}</span>
                  <span>Booking state: {r.booking_summary?.status}</span>
                  <span>Ended early: {r.booking_summary?.ended_early_at ? new Date(r.booking_summary.ended_early_at).toLocaleString() : "—"}</span>
                  <span>Reported at: {new Date(r.created_at).toLocaleString()}</span>
                </div>
                {(r.hq_actions || []).length > 0 && (
                  <div className="mt-3 border-t border-cream/10 pt-2">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-cream/40">HQ actions</p>
                    {r.hq_actions.map((a, i) => (
                      <p key={i} className="text-[10px] text-cream/70">{new Date(a.at).toLocaleString()} · {a.action}{a.reason ? ` — "${a.reason}"` : ""}</p>
                    ))}
                  </div>
                )}
              </div>
              <span className={`chip text-[10px] ${r.hq_status === "resolved" ? "border-emerald-400/40 text-emerald-300" : r.hq_status === "escalated" ? "border-red-400/40 text-red-300" : ""}`}>{r.hq_status}</span>
            </div>
            {r.hq_status !== "resolved" && (
              <div className="mt-4 border-t border-cream/10 pt-3 space-y-2">
                <input placeholder="Reason (required for escalate / restrict / suspend)"
                       data-testid={`hq-safety-reason-${r.id}`}
                       value={reason[r.id] || ""} onChange={(e) => setReason({ ...reason, [r.id]: e.target.value })}
                       className="w-full bg-transparent border border-cream/15 px-2 py-1 text-xs focus:border-gold outline-none" />
                <div className="flex gap-2 flex-wrap">
                  {ACTIONS.map((a) => (
                    <button key={a.id} data-testid={`hq-safety-action-${a.id}-${r.id}`}
                            onClick={() => act(r.id, a.id)}
                            className={`chip text-[10px] hover:border-gold ${a.danger ? "border-red-300/30 text-red-300 hover:border-red-300" : ""}`}>
                      {a.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
